import type { Theme } from '@/store/activity-store'

/**
 * Canvas-rendered libraries (ECharts, MapLibre GL paint properties) can't
 * consume CSS custom properties the way Tailwind classes can -- there is
 * no `hsl(var(--foreground))` equivalent available to a `<canvas>` 2D/WebGL
 * context. These literal values mirror `index.css`'s `--*` tokens exactly
 * so the chart/map still respects the active theme instead of hardcoding
 * an unrelated color; `index.css` remains the single source of truth for
 * every DOM-rendered color, and this is the deliberate, documented mirror
 * of it for the two canvas-based widgets. If `index.css`'s palette ever
 * changes, update both together.
 */
export interface ThemeColors {
  foreground: string
  mutedForeground: string
  border: string
  primary: string
  destructive: string
  warning: string
  accent: string
  card: string
}

// Comma-separated `hsl(h, s%, l%)` on purpose, not the space-separated
// CSS Color 4 form `index.css` uses: ECharts' (zrender's) color parser
// only understands the comma form. The canvas itself accepts both, so a
// space-separated color renders fine at first, but any derived color
// ECharts computes from it (e.g. a series' hover/emphasis color) parses to
// nothing and the element turns invisible on hover.
const LIGHT: ThemeColors = {
  foreground: 'hsl(240, 10%, 4%)',
  mutedForeground: 'hsl(240, 4%, 46%)',
  border: 'hsl(240, 6%, 90%)',
  primary: 'hsl(217, 91%, 50%)',
  destructive: 'hsl(0, 72%, 51%)',
  warning: 'hsl(38, 92%, 50%)',
  accent: 'hsl(240, 5%, 96%)',
  card: 'hsl(0, 0%, 100%)',
}

const DARK: ThemeColors = {
  foreground: 'hsl(0, 0%, 95%)',
  mutedForeground: 'hsl(240, 5%, 65%)',
  border: 'hsl(240, 6%, 18%)',
  primary: 'hsl(217, 91%, 60%)',
  destructive: 'hsl(0, 63%, 50%)',
  warning: 'hsl(38, 92%, 55%)',
  accent: 'hsl(240, 6%, 16%)',
  card: 'hsl(240, 9%, 9%)',
}

export function getThemeColors(theme: Theme): ThemeColors {
  return theme === 'dark' ? DARK : LIGHT
}

/** `hsl(h, s%, l%)` -> `hsla(h, s%, l%, alpha)`. */
export function withAlpha(color: string, alpha: number): string {
  return color.replace(/^hsl\((.*)\)$/, `hsla($1, ${alpha})`)
}
